import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Check, Globe, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import SEOPreview from '../components/SEOPreview';

interface PageSEO {
  id: string;
  page_id: string;
  meta_title: string;
  meta_description: string;
  og_image_url: string;
}

const DEFAULT_PAGES = [
  { id: 'home', name: 'Accueil', path: '/' },
  { id: 'pricing', name: 'Tarifs', path: '/pricing' },
  { id: 'resources', name: 'Ressources', path: '/resources' },
];

const AdminSEO: React.FC = () => {
  const [pages, setPages] = useState<PageSEO[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>('home');
  const [formData, setFormData] = useState<Omit<PageSEO, 'id'>>({
    page_id: 'home',
    meta_title: '',
    meta_description: '',
    og_image_url: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const checkUser = async () => {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsLoading(false);
    };
    checkUser();
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return;

    const fetchPages = async () => {
      try {
        const { data, error } = await supabase
          .from('pages_seo')
          .select('*');

        if (error) throw error;
        setPages(data || []);

        // Set initial form data if page exists
        const selectedPageData = data?.find(p => p.page_id === selectedPage);
        if (selectedPageData) {
          setFormData({
            page_id: selectedPageData.page_id,
            meta_title: selectedPageData.meta_title,
            meta_description: selectedPageData.meta_description,
            og_image_url: selectedPageData.og_image_url
          });
        }
      } catch (error) {
        console.error('Error fetching pages:', error);
        setError('Erreur lors du chargement des pages');
      }
    };

    fetchPages();
  }, [selectedPage, user]);

  const handlePageChange = (pageId: string) => {
    setSelectedPage(pageId);
    const pageData = pages.find(p => p.page_id === pageId);
    if (pageData) {
      setFormData({
        page_id: pageData.page_id,
        meta_title: pageData.meta_title,
        meta_description: pageData.meta_description,
        og_image_url: pageData.og_image_url
      });
    } else {
      setFormData({
        page_id: pageId,
        meta_title: '',
        meta_description: '',
        og_image_url: ''
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const { error } = await supabase
        .from('pages_seo')
        .upsert({
          page_id: formData.page_id,
          meta_title: formData.meta_title,
          meta_description: formData.meta_description,
          og_image_url: formData.og_image_url
        });

      if (error) throw error;

      setSuccess('Paramètres SEO sauvegardés avec succès');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving SEO settings:', error);
      setError('Erreur lors de la sauvegarde des paramètres SEO');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-10">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Accès restreint</h3>
        <p className="text-gray-600">
          Vous n'avez pas les droits nécessaires pour accéder à cette page.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Paramètres SEO</h1>
        <Globe className="w-6 h-6 text-gray-400" />
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sélectionner une page
          </label>
          <select
            value={selectedPage}
            onChange={(e) => handlePageChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {DEFAULT_PAGES.map((page) => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre SEO
            </label>
            <input
              type="text"
              value={formData.meta_title}
              onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Titre optimisé pour le référencement"
            />
            <p className="mt-1 text-sm text-gray-500">
              {formData.meta_title.length}/60 caractères recommandés
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meta Description
            </label>
            <textarea
              value={formData.meta_description}
              onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={3}
              placeholder="Description qui apparaîtra dans les résultats de recherche"
            />
            <p className="mt-1 text-sm text-gray-500">
              {formData.meta_description.length}/160 caractères recommandés
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image OpenGraph
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={formData.og_image_url}
                onChange={(e) => setFormData({ ...formData, og_image_url: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="URL de l'image pour les réseaux sociaux"
              />
              <button
                type="button"
                className="px-3 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200"
              >
                <ImageIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Format recommandé : 1200x630px
            </p>
          </div>

          <div className="mt-8">
            <SEOPreview
              url={`https://pimpmyjob.fr${DEFAULT_PAGES.find(p => p.id === selectedPage)?.path}`}
              title={formData.meta_title}
              description={formData.meta_description}
              imageUrl={formData.og_image_url}
            />
          </div>

          <div className="pt-6 flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSEO;